import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from '@mui/material';
import {
  DataGridPro as DataGrid,
  GridActionsCellItem,
} from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { sortBy } from 'lodash-es';
import * as service from '../../services/admin-service';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { Dialog } from '../../components/Dialog';
import { useAlerts } from '../../hooks/use-alerts';

export function FeatureFlags() {
  const { showError } = useAlerts();
  const [featureFlags, setFeatureFlags] = useState();
  const [newUser, setNewUser] = useState('');
  const [flagToAdd, setFlagToAdd] = useState();
  const [flagToToggle, setFlagToToggle] = useState();
  const [flagToDelete, setFlagToDelete] = useState();

  useEffect(() => {
    service.getFeatureFlags().then(setFeatureFlags);
  }, []);

  return (
    <>
      {buildNewFlag()}
      {buildToggleFlag()}
      {buildDeleteFlag()}
      <Box
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '& .MuiDataGrid-detailPanel': {
            background: '#eee',
          },
          '& .MuiDataGrid-booleanCell[data-value="true"]': {
            color: (theme) => `${theme.palette.primary.main} !important`,
          },
          '& .MuiDataGrid-booleanCell[data-value="false"]': {
            color: (theme) => `${theme.palette.error.main} !important`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mb: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => setFlagToAdd({})}
          >
            Add Feature Flag
          </Button>
        </Box>
        <DataGrid
          sx={{ flex: 1 }}
          loading={!featureFlags}
          columns={buildColumns()}
          rows={featureFlags || []}
          getRowId={(row) => row.name}
          getDetailPanelHeight={() => 'auto'}
          getDetailPanelContent={buildUsers}
          initialState={{
            sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
          }}
        />
      </Box>
    </>
  );

  function buildToggleFlag() {
    if (!flagToToggle) return null;

    return (
      <ConfirmationDialog
        title={flagToToggle.name}
        titleIcon={false}
        message={`Are you sure you want to ${flagToToggle.enabled ? 'disable' : 'enable'} ${flagToToggle.name}?`}
        handleClose={() => setFlagToToggle(false)}
        handleConfirm={() => toggleGlobally(flagToToggle)}
      />
    );
  }

  async function deleteFlag() {
    try {
      setFlagToDelete(false);
      await service.deleteFeatureFlag(flagToDelete);
      setFeatureFlags((current) =>
        current.filter((f) => f.name !== flagToDelete.name),
      );
    } catch (e) {
      showError(e);
    }
  }

  function buildDeleteFlag() {
    return flagToDelete ? (
      <ConfirmationDialog
        title={flagToDelete.name}
        message={`Are you sure you want to DELETE ${flagToDelete.name} FOREVER?`}
        handleClose={() => setFlagToDelete(false)}
        handleConfirm={deleteFlag}
      />
    ) : null;
  }

  function buildNewFlag() {
    return flagToAdd ? (
      <NewFlag
        onCancel={() => setFlagToAdd(false)}
        onSave={(flag) => {
          setFeatureFlags((current) => [...current, flag]);
          setFlagToAdd(false);
        }}
      />
    ) : null;
  }

  function addNewUser(flag, event) {
    setNewUser('');
    const user = event.target.value.trim();
    if (user) upsertUser(flag.name, user, true);
  }

  function buildUsers({ row: flag }) {
    return (
      <TableContainer
        component={Paper}
        sx={{
          width: 'auto',
          m: 3,
          borderRadius: 1,
          display: 'inline-table',
        }}
      >
        <Table sx={{ width: 'auto' }} size="small">
          <TableBody>
            {sortBy(flag.users, 'userId').map((user) => (
              <TableRow key={user.userId}>
                <TableCell>{user.userId}</TableCell>
                <TableCell>
                  <Checkbox
                    size="small"
                    checked={user.enabled}
                    onChange={(e) =>
                      upsertUser(flag.name, user.userId, e.target.checked)
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <TextField
                  sx={{ width: 200 }}
                  size="small"
                  label="New User"
                  value={newUser}
                  onChange={(e) => setNewUser(e.target.value)}
                  onBlur={addNewUser.bind(null, flag)}
                />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  function getUpdater(enabled) {
    return enabled ? service.disableFeatureFlag : service.enableFeatureFlag;
  }

  function upsertUser(flag, user, enabled) {
    getUpdater(!enabled)(flag, user).then(refreshFlag);
  }

  function toggleGlobally(flag) {
    setFlagToToggle(false);
    getUpdater(flag.enabled)(flag.name).then(refreshFlag);
  }

  function refreshFlag(flag) {
    setFeatureFlags((current) =>
      current.map((currentFlag) =>
        currentFlag.name === flag.name ? flag : currentFlag,
      ),
    );
  }

  function buildColumns() {
    return [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 240,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
      },
      {
        field: 'insertDate',
        headerName: 'Created On',
        type: 'date',
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : '',
      },
      {
        field: 'insertUser',
        headerName: 'Created By',
      },
      {
        field: 'updateDate',
        headerName: 'Updated On',
        type: 'date',
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : '',
      },
      {
        field: 'updateUser',
        headerName: 'Updated By',
      },
      {
        field: 'userCount',
        headerName: 'Users',
        valueGetter: ({ row }) => row.users.length,
        type: 'number',
      },
      {
        field: 'enabled',
        headerName: 'Enabled?',
        type: 'boolean',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="toggle"
            showInMenu
            label={row.enabled ? 'Disable Globally' : 'Enable Globally'}
            onClick={() => setFlagToToggle(row)}
          />,
          <GridActionsCellItem
            key="delete"
            showInMenu
            label="Delete"
            onClick={() => setFlagToDelete(row)}
          />,
        ],
      },
    ];
  }
}

function NewFlag(props) {
  const [flag, setFlag] = useState({});
  const { showError } = useAlerts();

  return (
    <Dialog
      {...props}
      width="sm"
      title="New Feature Flag"
      canSave={Boolean(flag.name && flag.description)}
      onSave={() =>
        service.addFeatureFlag(flag).then(props.onSave).catch(showError)
      }
    >
      <TextField
        label="Name"
        value={flag.name || ''}
        sx={{ mt: 2 }}
        onChange={(e) => setFlag({ ...flag, name: e.target.value })}
      />
      <TextField
        label="Description"
        value={flag.description || ''}
        sx={{ mt: 2 }}
        onChange={(e) => setFlag({ ...flag, description: e.target.value })}
      />
    </Dialog>
  );
}
